/**
 * Orthogonal routing for the links the layout engine does not route: links between nodes in
 * different groups, which ELK never sees because it lays each group out as its own flat graph,
 * and every link of a `manual` layout.
 *
 * A sparse grid of candidate lines is built from the obstacles near the two ends and searched
 * with Dijkstra over (grid point, heading), so a turn has a price.  One cost model covers the
 * rest: keep off nodes and titles, do not lie along another link or land on its arrowhead, prefer
 * the middle of a gutter, cross as little as possible, turn as little as possible, be short.
 *
 * Pure geometry over plain data.  Deterministic for a given input, which the crossing indicators
 * depend on.
 */
import type { HandlePosition, LayoutRect, Point } from "./layout";

/** Something a route should not be drawn underneath. */
export type Obstacle = LayoutRect & { id: string };

/**
 * A group's heading strip: a route may cross it, at a small price per pixel, so it enters a band
 * where it must but does not travel along inside the band's top edge.  The title itself is solid.
 */
export type SoftRect = Obstacle;

/** One end of a link: the node it attaches to and the sides it may use. */
export type PortRequest = {
    nodeId: string;
    rect: LayoutRect;
    /** In order of preference.  A single entry pins the side. */
    sides: HandlePosition[];
    /** Where along the side to attach, when a fan-in has already agreed on a point.  Pins the first side listed. */
    fixedAlong?: number;
};

export type RouteRequest = {
    source: PortRequest;
    target: PortRequest;
    /** Leaf nodes other than the two endpoints. */
    solid: Obstacle[];
    soft: SoftRect[];
    /** Group boxes.  Never obstacles, but the gaps between them are where a route wants to run. */
    channels: LayoutRect[];
    occupancy: Occupancy;
    /** Minimum gap between two links running alongside each other. */
    edgesep: number;
    /** Bounding box of everything drawn. */
    chart: LayoutRect;
};

export type RouteResult = {
    points: Point[];
    sourceSide: HandlePosition;
    targetSide: HandlePosition;
    /**
     * How far the router had to relax to find this.  `0` is the normal case; `1` widened the
     * search to the whole chart; `2` allowed passing under nodes; `3` did not search at all.
     */
    relaxed: 0 | 1 | 2 | 3;
    /** What the route cost under the model, for tests and tuning.  Zero for an unsearched route. */
    cost: number;
};

/** How much room to leave between a route and anything it passes. */
export const OBSTACLE_CLEARANCE = 8;

/**
 * How far a link runs straight out of a node before it may turn.  Longer than `CORNER_RADIUS`
 * plus the inset `seatEndpoints` takes, so the corner at the end of a stub is as round as any.
 */
export const ROUTE_STUB = 28;

/**
 * How close to a node's corner a link may attach.  Seated in the corner it reads as arriving
 * diagonally, and on a chevron or a triangle the corner is where the shape runs out.
 */
export const ARRIVAL_MARGIN = 16;

/** Pitch between attachment points along one side: enough to keep two arrowheads apart. */
export const ARROWHEAD_CLEARANCE = 12;

/**
 * Grid lines closer together than this are one line, or a route using both would jog between them:
 * two bends a few pixels apart that no corner radius can round.  Below the slot pitch, so two
 * attachment slots never merge.
 */
export const LINE_MERGE_DISTANCE = 6;

/**
 * The shortest run kept between two bends: twice `CORNER_RADIUS` (edgeTypes/path.ts), so both its
 * corners can be drawn at full radius.  One shorter run pulls every corner on its route in.
 */
export const MIN_JOG = 48;

/** Two links closer than this read as one line; between here and `edgesep` they are merely cramped. */
export const MIN_LANE = ARROWHEAD_CLEARANCE;

/** How far past its ends a drawn run still claims its line: two runs on one line with a small gap read as one broken line. */
export const LANE_END_MARGIN = 2 * MIN_LANE;

/**
 * How much more an authored `positionStart` / `positionEnd` may cost than the free route before it
 * is set aside with a console warning.  Below one crossing, so a pin that forces a hop gives way.
 */
export const PIN_OVERRIDE = 200;

/** How far beyond its two ends a route may look for a way round: a node's width plus a gap either side. */
export const DETOUR_MARGIN = 120;

/**
 * The cost model, in pixels of route length.  The ordering is what matters: a node is a wall until
 * nothing else works; a crossing costs about four bends; a bend costs more than a side the flow
 * does not favour; sharing a lane is priced per pixel, so a short overlap is tolerated and a long
 * one is not; hugging an obstacle, leaving the chart and running along a heading are mild.
 */
const COST = {
    bend: 60,
    /** Attaching by a side other than the one facing the other end.  Small, so a blocked side is still given up. */
    side: 40,
    /** Crossing another link.  Drawn as a hop, but still something the eye must resolve. */
    cross: 250,
    /** Attaching within an arrowhead of a point another link already uses. */
    portTaken: 150,
    /** Per pixel alongside another link, scaled by `factor` for distance. */
    lane: 4,
    hug: 0.5,
    /** Per pixel outside the chart: a loop round the outside is allowed, at double price. */
    outside: 1,
    /** Per pixel off a gap's midline.  A tie-breaker only, so equal runs settle down the middle of a gutter. */
    offChannel: 0.05,
    heading: 0.25,
    /** Per pixel off the middle of a side.  Above the length it saves, or every arrival sits one pitch off-centre. */
    slotOffset: 1.5,
    solidFlat: 2000,
    solidPerPixel: 10,
};

/** Outward normal of each side, in screen coordinates (y grows downwards). */
export const SIDE_NORMALS: Record<HandlePosition, Point> = {
    top: { x: 0, y: -1 },
    bottom: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

/** The axis a point on this side varies along. */
export const alongAxis = (side: HandlePosition): "x" | "y" => (side === "top" || side === "bottom" ? "x" : "y");

/** Midpoint of one side of a box. */
export const sideMidpoint = (rect: LayoutRect, side: HandlePosition): Point => ({
    top: { x: rect.x + rect.width / 2, y: rect.y },
    bottom: { x: rect.x + rect.width / 2, y: rect.y + rect.height },
    left: { x: rect.x, y: rect.y + rect.height / 2 },
    right: { x: rect.x + rect.width, y: rect.y + rect.height / 2 },
}[side]);

/** The point on a side at a given coordinate along it. */
export const sidePoint = (rect: LayoutRect, side: HandlePosition, along: number): Point => ({
    top: { x: along, y: rect.y },
    bottom: { x: along, y: rect.y + rect.height },
    left: { x: rect.x, y: along },
    right: { x: rect.x + rect.width, y: along },
}[side]);

/** Drop consecutive duplicates and points that sit on a straight run. */
export const simplify = (points: Point[]): Point[] => {
    const out: Point[] = [];

    for (const point of points) {
        const last = out[out.length - 1];
        if (last && Math.abs(last.x - point.x) < 0.5 && Math.abs(last.y - point.y) < 0.5) {
            continue;
        }
        out.push(point);
    }

    return out.filter((point, i) => {
        if (i === 0 || i === out.length - 1) return true;
        const before = out[i - 1];
        const after = out[i + 1];
        const collinearX = Math.abs(before.x - point.x) < 0.5 && Math.abs(point.x - after.x) < 0.5;
        const collinearY = Math.abs(before.y - point.y) < 0.5 && Math.abs(point.y - after.y) < 0.5;
        return !(collinearX || collinearY);
    });
};

/** The box a straight run sweeps out. */
export const runBounds = (from: Point, to: Point): LayoutRect => {
    const x = Math.min(from.x, to.x);
    const y = Math.min(from.y, to.y);

    return { x, y, width: Math.abs(to.x - from.x), height: Math.abs(to.y - from.y) };
};

/** Does a run's box overlap one obstacle?  Strict, so a run along an obstacle's edge is clear. */
export const runHits = (run: LayoutRect, o: LayoutRect) =>
    run.x < o.x + o.width && run.x + run.width > o.x && run.y < o.y + o.height && run.y + run.height > o.y;

/** Does a straight run between two points clear every obstacle? */
export const runIsClear = (obstacles: LayoutRect[], from: Point, to: Point) => {
    const run = runBounds(from, to);

    return !obstacles.some(o => runHits(run, o));
};

/** Does a whole polyline clear every obstacle? */
export const routeIsClear = (obstacles: LayoutRect[], points: Point[]) =>
    points.every((point, i) => i === 0 || runIsClear(obstacles, points[i - 1], point));

type Segment = { id: string; axis: "h" | "v"; at: number; from: number; to: number };

/**
 * What has already been drawn, so the next route keeps clear of it.  The engine's routes are
 * recorded first, then each routed link as it commits, in declaration order -- the order that also
 * decides which link is painted over which.
 */
export class Occupancy {
    segments: Segment[] = [];
    private ports: { id: string; key: string; along: number }[] = [];

    /** Record every axis-aligned run of a route.  A stub is a run like any other. */
    claimRoute(id: string, points: Point[]) {
        for (let i = 1; i < points.length; i++) {
            const a = points[i - 1];
            const b = points[i];
            const horizontal = Math.abs(a.y - b.y) < 0.5;
            const vertical = Math.abs(a.x - b.x) < 0.5;
            if (horizontal && !vertical) {
                this.segments.push({ id, axis: "h", at: a.y, from: Math.min(a.x, b.x), to: Math.max(a.x, b.x) });
            } else if (vertical && !horizontal) {
                this.segments.push({ id, axis: "v", at: a.x, from: Math.min(a.y, b.y), to: Math.max(a.y, b.y) });
            }
        }
    }

    /** Record where a link meets a node, arriving or leaving, so the next one does not land on it. */
    claimPort(id: string, nodeId: string, side: HandlePosition, along: number) {
        this.ports.push({ id, key: `${nodeId}:${side}`, along });
    }

    /** Where links meet this side, leaving out any whose id `except` accepts (a fan's own members). */
    portClaims(nodeId: string, side: HandlePosition, except?: (id: string) => boolean): number[] {
        const key = `${nodeId}:${side}`;
        return this.ports.filter(port => port.key === key && !except?.(port.id)).map(port => port.along);
    }

    /** Forget one link, so it can be routed again with every other link in place. */
    release(id: string) {
        this.segments = this.segments.filter(segment => segment.id !== id);
        this.ports = this.ports.filter(port => port.id !== id);
    }
}

export type Slot = { along: number; cost: number };

/**
 * Where along one side a link may attach, cheapest first: the middle, then either side of it at
 * arrowhead pitch, as far as the margin allows.  A slot another link uses is priced, not excluded.
 */
export const portSlots = (
    rect: LayoutRect,
    side: HandlePosition,
    occupancy: Occupancy,
    nodeId: string,
    fixedAlong?: number
): Slot[] => {
    if (fixedAlong !== undefined) {
        return [{ along: fixedAlong, cost: 0 }];
    }

    const axis = alongAxis(side);
    const size = axis === "x" ? rect.width : rect.height;
    const low = rect[axis] + ARRIVAL_MARGIN;
    const high = rect[axis] + size - ARRIVAL_MARGIN;
    const middle = rect[axis] + size / 2;
    const claims = occupancy.portClaims(nodeId, side);

    const slots: Slot[] = [];
    const add = (along: number) => {
        if (along < low - 0.25 || along > high + 0.25) {
            return false;
        }
        const taken = claims.some(claim => Math.abs(claim - along) < ARROWHEAD_CLEARANCE - 0.5);
        slots.push({ along, cost: COST.slotOffset * Math.abs(along - middle) + (taken ? COST.portTaken : 0) });
        return true;
    };

    // The middle always exists, however short the side: a link has to attach somewhere.
    const taken = claims.some(claim => Math.abs(claim - middle) < ARROWHEAD_CLEARANCE - 0.5);
    slots.push({ along: middle, cost: taken ? COST.portTaken : 0 });
    for (let k = 1; ; k++) {
        const after = add(middle + k * ARROWHEAD_CLEARANCE);
        const before = add(middle - k * ARROWHEAD_CLEARANCE);
        if (!after && !before) break;
    }

    return slots.sort((a, b) => a.cost - b.cost || Math.abs(a.along - middle) - Math.abs(b.along - middle) || a.along - b.along);
};

/** One candidate attachment: the side, the point on it, and the point the stub reaches. */
export type Port = Slot & { side: HandlePosition; point: Point; stub: Point };

/**
 * How far a stub can run out of this point: `ROUTE_STUB` where there is room, else a clearance
 * short of the neighbour ahead, or halfway to it, so the stub never ends inside a node.
 */
const stubLength = (point: Point, side: HandlePosition, solid: LayoutRect[]): number => {
    const normal = SIDE_NORMALS[side];
    let gap = Infinity;
    for (const o of solid) {
        const ahead = normal.x
            ? (normal.x > 0 ? o.x - point.x : point.x - (o.x + o.width))
            : (normal.y > 0 ? o.y - point.y : point.y - (o.y + o.height));
        const alongside = normal.x
            ? point.y > o.y && point.y < o.y + o.height
            : point.x > o.x && point.x < o.x + o.width;
        if (alongside && ahead >= 0 && ahead < gap) {
            gap = ahead;
        }
    }

    if (gap >= ROUTE_STUB + OBSTACLE_CLEARANCE) {
        return ROUTE_STUB;
    }
    return Math.max(2, Math.min(gap - OBSTACLE_CLEARANCE, ROUTE_STUB), Math.min(gap / 2, ROUTE_STUB));
};

export const portsOf = (end: PortRequest, occupancy: Occupancy, solid: LayoutRect[] = []): Port[] =>
    // A fixed point only means something on one side, so it pins the side as well.
    (end.fixedAlong === undefined ? end.sides : end.sides.slice(0, 1)).flatMap((side, rank) => portSlots(end.rect, side, occupancy, end.nodeId, end.fixedAlong).map((slot) => {
        const point = sidePoint(end.rect, side, slot.along);
        const stub = stubLength(point, side, solid);
        return {
            ...slot,
            cost: slot.cost + (rank ? COST.side : 0),
            side,
            point,
            stub: { x: point.x + SIDE_NORMALS[side].x * stub, y: point.y + SIDE_NORMALS[side].y * stub },
        };
    }));

const snap = (value: number) => Math.round(value * 2) / 2;

const overlap = (aFrom: number, aTo: number, bFrom: number, bTo: number) =>
    Math.max(0, Math.min(aTo, bTo) - Math.max(aFrom, bFrom));

const intersects = (a: LayoutRect, b: LayoutRect) =>
    a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;

const expand = (rect: LayoutRect, by: number): LayoutRect =>
    ({ x: rect.x - by, y: rect.y - by, width: rect.width + 2 * by, height: rect.height + 2 * by });

const union = (a: LayoutRect, b: LayoutRect): LayoutRect => {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
};

const clamp = (rect: LayoutRect, within: LayoutRect): LayoutRect => {
    const x = Math.max(rect.x, within.x);
    const y = Math.max(rect.y, within.y);
    return {
        x, y,
        width: Math.max(0, Math.min(rect.x + rect.width, within.x + within.width) - x),
        height: Math.max(0, Math.min(rect.y + rect.height, within.y + within.height) - y),
    };
};

/** Midpoints of the gaps between a set of intervals, once overlapping ones are merged. */
const gapMidpoints = (intervals: [number, number][]): number[] => {
    const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
    const merged: [number, number][] = [];
    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && interval[0] <= last[1]) {
            last[1] = Math.max(last[1], interval[1]);
        } else {
            merged.push([interval[0], interval[1]]);
        }
    }

    const mids: number[] = [];
    for (let i = 1; i < merged.length; i++) {
        mids.push((merged[i - 1][1] + merged[i][0]) / 2);
    }
    return mids;
};

/**
 * The grid lines worth routing along, inside one box: a clearance outside every obstacle, the
 * midpoint of every gap between obstacles and between groups, lanes beside every link already
 * drawn, and the end of every candidate stub.
 */
export const collectLines = (request: RouteRequest, solid: Obstacle[], box: LayoutRect, ports: Port[]) => {
    /**
     * An `anchor` (a stub's end, a clearance line) is exact; other lines within
     * `LINE_MERGE_DISTANCE` of one fold into it, and into each other.
     */
    type Candidate = { at: number; anchor: boolean; channel: boolean };
    const xs: Candidate[] = [];
    const ys: Candidate[] = [];
    const add = (list: Candidate[], lo: number, hi: number) => (value: number, anchor = false, channel = false) => {
        const inside = Number.isFinite(value) && value >= lo - 0.25 && value <= hi + 0.25;
        if (inside) list.push({ at: snap(value), anchor, channel });
        return inside;
    };
    const addX = add(xs, box.x, box.x + box.width);
    const addY = add(ys, box.y, box.y + box.height);

    addX(box.x); addX(box.x + box.width);
    addY(box.y); addY(box.y + box.height);

    const near = solid.filter(o => intersects(o, box));
    for (const o of near) {
        addX(o.x - OBSTACLE_CLEARANCE, true); addX(o.x + o.width + OBSTACLE_CLEARANCE, true);
        addY(o.y - OBSTACLE_CLEARANCE, true); addY(o.y + o.height + OBSTACLE_CLEARANCE, true);
    }

    for (const set of [near, request.channels.filter(c => intersects(c, box))]) {
        gapMidpoints(set.map(o => [o.x, o.x + o.width])).forEach(mid => addX(mid, false, true));
        gapMidpoints(set.map(o => [o.y, o.y + o.height])).forEach(mid => addY(mid, false, true));
    }

    // Lanes beside every drawn link: the full spacing, and the cramped ones a narrow gutter still offers.
    const lanes = [MIN_LANE, request.edgesep / 2, request.edgesep].filter((d, i, all) => d >= MIN_LANE && all.indexOf(d) === i);
    for (const segment of request.occupancy.segments) {
        if (segment.axis === "h") {
            if (segment.to >= box.x && segment.from <= box.x + box.width) {
                for (const d of lanes) { addY(segment.at - d); addY(segment.at + d); }
            }
        } else if (segment.to >= box.y && segment.from <= box.y + box.height) {
            for (const d of lanes) { addX(segment.at - d); addX(segment.at + d); }
        }
    }

    for (const port of ports) {
        addX(port.stub.x, true);
        addY(port.stub.y, true);
    }

    /** Sorted, deduplicated, with near-coincident soft lines folded into their neighbours. */
    const merge = (candidates: Candidate[]) => {
        const sorted = candidates.slice().sort((a, b) => a.at - b.at || Number(b.anchor) - Number(a.anchor));
        const kept: Candidate[] = [];
        for (const candidate of sorted) {
            const last = kept[kept.length - 1];
            if (last && candidate.at - last.at < LINE_MERGE_DISTANCE && !(candidate.anchor && last.anchor && candidate.at !== last.at)) {
                // Fold into the kept line.  An anchor takes over a soft line's position; anything
                // else keeps the earlier position.  Two distinct anchors both stand.
                if (candidate.anchor && !last.anchor) {
                    last.at = candidate.at;
                    last.anchor = true;
                }
                last.channel ||= candidate.channel;
                continue;
            }
            kept.push({ ...candidate });
        }
        return kept;
    };

    const mergedX = merge(xs);
    const mergedY = merge(ys);
    return {
        xs: mergedX.map(c => c.at),
        ys: mergedY.map(c => c.at),
        channels: {
            xs: new Set(mergedX.filter(c => c.channel).map(c => c.at)),
            ys: new Set(mergedY.filter(c => c.channel).map(c => c.at)),
        },
    };
};

/**
 * Remove any run shorter than `MIN_JOG` between two bends, by sliding one of the runs either side
 * of it onto the other's line: the one whose far end is free, else the shorter.  A run ending on a
 * node never moves, and a slide that would put a run under a node is not made.
 */
export const straighten = (points: Point[], solid: LayoutRect[]): Point[] => {
    let route = simplify(points);

    for (let pass = 0; pass < route.length; pass++) {
        let changed = false;

        for (let i = 1; i < route.length - 2; i++) {
            const a = route[i];
            const b = route[i + 1];
            const jog = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
            if (jog >= MIN_JOG || jog < 0.5) continue;

            // The jog runs along `axis`; the runs either side of it are square to that.
            const axis: "x" | "y" = Math.abs(a.x - b.x) > Math.abs(a.y - b.y) ? "x" : "y";
            const beforeFree = i - 1 > 0;
            const afterFree = i + 2 < route.length - 1;
            if (!beforeFree && !afterFree) continue;

            const beforeLength = Math.abs(route[i - 1].x - a.x) + Math.abs(route[i - 1].y - a.y);
            const afterLength = Math.abs(route[i + 2].x - b.x) + Math.abs(route[i + 2].y - b.y);
            const moveBefore = beforeFree && (!afterFree || beforeLength <= afterLength);

            const moved = route.map(point => ({ ...point }));
            if (moveBefore) {
                moved[i - 1][axis] = b[axis];
                moved[i][axis] = b[axis];
            } else {
                moved[i + 1][axis] = a[axis];
                moved[i + 2][axis] = a[axis];
            }

            const from = moveBefore ? i - 2 : i;
            const touched = moved.slice(Math.max(0, from), Math.min(moved.length, from + 4));
            if (!routeIsClear(solid, touched)) continue;

            route = simplify(moved);
            changed = true;
            break;
        }

        if (!changed) break;
    }

    return route;
};

/** Everything that can price a run along one grid line, gathered once per line. */
type LineInfo = {
    /** Obstacles whose interior the line passes through, as spans along the line. */
    blockers: [number, number][];
    /** Heading strips the line passes through, as spans along the line. */
    softs: [number, number][];
    /** Links running alongside the line, with how close. */
    parallel: { from: number; to: number; factor: number }[];
    /** Links running across the line, at which coordinate. */
    crossing: number[];
    /** Obstacles this line runs a clearance away from, as spans along the line. */
    hugs: [number, number][];
    /** Whether the line lies outside the chart altogether. */
    outside: boolean;
    /** Whether the line is the middle of a gap between obstacles or groups. */
    channel: boolean;
    /** The chart's extent along the line, for pricing the part of a run that leaves it. */
    inside: [number, number];
};

/** Headings, as unit steps: right, down, left, up.  A turn is `+1` or `+3` modulo 4. */
const HEADINGS = [{ x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 }];

/** A binary min-heap of (cost, sequence, state).  Sequence breaks ties, so the search is deterministic. */
class Heap {
    private readonly items: [number, number, number][] = [];
    private sequence = 0;

    get size() { return this.items.length; }

    push(cost: number, state: number) {
        const items = this.items;
        items.push([cost, this.sequence++, state]);
        let i = items.length - 1;
        while (i > 0) {
            const parent = (i - 1) >> 1;
            if (this.less(items[i], items[parent])) {
                [items[i], items[parent]] = [items[parent], items[i]];
                i = parent;
            } else break;
        }
    }

    pop(): [number, number, number] {
        const items = this.items;
        const top = items[0];
        const last = items.pop()!;
        if (items.length) {
            items[0] = last;
            let i = 0;
            for (;;) {
                const left = 2 * i + 1;
                const right = left + 1;
                let smallest = i;
                if (left < items.length && this.less(items[left], items[smallest])) smallest = left;
                if (right < items.length && this.less(items[right], items[smallest])) smallest = right;
                if (smallest === i) break;
                [items[i], items[smallest]] = [items[smallest], items[i]];
                i = smallest;
            }
        }
        return top;
    }

    private less(a: [number, number, number], b: [number, number, number]) {
        return a[0] < b[0] || (a[0] === b[0] && a[1] < b[1]);
    }
}

type Attempt = { points: Point[]; sourceSide: HandlePosition; targetSide: HandlePosition; cost: number };

/**
 * One search: build the grid inside `box`, run Dijkstra from every source stub to every target
 * stub, and hand back the cheapest route -- or nothing, if no route exists under these rules.
 */
const attempt = (
    request: RouteRequest,
    solid: Obstacle[],
    box: LayoutRect,
    hard: boolean,
    sources: Port[],
    targets: Port[]
): Attempt | undefined => {
    const { xs, ys, channels } = collectLines(request, solid, box, [...sources, ...targets]);
    const nx = xs.length;
    const ny = ys.length;
    if (nx < 2 || ny < 2) {
        return undefined;
    }

    const indexOf = (values: number[], value: number) => {
        const target = snap(value);
        let lo = 0;
        let hi = values.length - 1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (values[mid] < target) lo = mid + 1;
            else if (values[mid] > target) hi = mid - 1;
            else return mid;
        }
        return -1;
    };

    const { soft, occupancy, edgesep } = request;
    const info = new Map<number, LineInfo>();
    /** Everything that prices a run along the `index`th line of `axis`, built on first use. */
    const lineInfo = (axis: "x" | "y", index: number): LineInfo => {
        const key = axis === "x" ? index : -1 - index;
        const cached = info.get(key);
        if (cached) return cached;

        // A line of constant `axis`: runs along it vary on the other axis.
        const at = axis === "x" ? xs[index] : ys[index];
        const constantSize = axis === "x" ? "width" : "height";
        const varying = axis === "x" ? "y" : "x";
        const varyingSize = axis === "x" ? "height" : "width";
        const { chart } = request;
        const built: LineInfo = {
            blockers: [], softs: [], parallel: [], crossing: [], hugs: [],
            outside: at < chart[axis] || at > chart[axis] + chart[constantSize],
            channel: (axis === "x" ? channels.xs : channels.ys).has(at),
            inside: [chart[varying], chart[varying] + chart[varyingSize]],
        };

        for (const o of solid) {
            // Grown by half a clearance, so a run cannot graze a node's edge while the clearance
            // lines themselves stay usable.
            const grow = OBSTACLE_CLEARANCE / 2;
            if (at > o[axis] - grow && at < o[axis] + o[constantSize] + grow) {
                built.blockers.push([o[varying] - grow, o[varying] + o[varyingSize] + grow]);
            }
            if (Math.abs(at - (o[axis] - OBSTACLE_CLEARANCE)) < 0.75
                || Math.abs(at - (o[axis] + o[constantSize] + OBSTACLE_CLEARANCE)) < 0.75) {
                built.hugs.push([o[varying], o[varying] + o[varyingSize]]);
            }
        }
        for (const o of soft) {
            if (at > o[axis] && at < o[axis] + o[constantSize]) {
                built.softs.push([o[varying], o[varying] + o[varyingSize]]);
            }
        }
        // A run along an x-line is vertical, so it is parallel to "v" segments and crosses "h" ones.
        const parallelAxis = axis === "x" ? "v" : "h";
        for (const segment of occupancy.segments) {
            if (segment.axis === parallelAxis) {
                const distance = Math.abs(segment.at - at);
                if (distance < edgesep) {
                    // Collinear is two links drawn as one line, priced at twice the base rate.
                    // Steep out to `MIN_LANE`, then an eighth of the base rate falling to nothing
                    // at `edgesep`: sharing a narrow gutter a lane apart must stay well under the
                    // price of a crossing, or the router hops instead of running alongside.
                    const factor = distance < 0.5
                        ? 2
                        : distance < MIN_LANE
                            ? 1 - 0.875 * (distance / MIN_LANE)
                            : 0.125 * (1 - (distance - MIN_LANE) / Math.max(1, edgesep - MIN_LANE));
                    built.parallel.push({ from: segment.from - LANE_END_MARGIN, to: segment.to + LANE_END_MARGIN, factor });
                }
            } else if (segment.from < at && segment.to > at) {
                built.crossing.push(segment.at);
            }
        }

        info.set(key, built);
        return built;
    };

    /** Price of one grid step along a line, or `Infinity` where it may not go. */
    const stepCost = (axis: "x" | "y", index: number, from: number, to: number): number => {
        const line = lineInfo(axis, index);
        const lo = Math.min(from, to);
        const hi = Math.max(from, to);
        let cost = hi - lo;

        for (const [bFrom, bTo] of line.blockers) {
            const shared = overlap(lo, hi, bFrom, bTo);
            if (shared > 0) {
                if (hard) return Infinity;
                cost += COST.solidFlat + COST.solidPerPixel * shared;
            }
        }
        for (const [sFrom, sTo] of line.softs) {
            cost += COST.heading * overlap(lo, hi, sFrom, sTo);
        }
        for (const p of line.parallel) {
            const shared = overlap(lo, hi, p.from, p.to);
            if (shared > 0) {
                cost += COST.lane * p.factor * shared;
            }
        }
        for (const c of line.crossing) {
            // Half-open: every drawn link lies on a grid line, so a crossing sits exactly on one
            // and is charged to the step that reaches it, once.
            if (c > lo && c <= hi) {
                cost += COST.cross;
            }
        }
        if (line.hugs.some(([hFrom, hTo]) => overlap(lo, hi, hFrom, hTo) > 0)) {
            cost += COST.hug * (hi - lo);
        }
        cost += COST.outside * (line.outside ? hi - lo : (hi - lo) - overlap(lo, hi, line.inside[0], line.inside[1]));
        if (!line.channel) {
            cost += COST.offChannel * (hi - lo);
        }

        return cost;
    };

    const states = nx * ny * 4;
    const END = states;
    const distance = new Float64Array(states + 1).fill(Infinity);
    const previous = new Int32Array(states + 1).fill(-1);
    const stateOf = (ix: number, iy: number, heading: number) => ((ix * ny) + iy) * 4 + heading;

    const heap = new Heap();
    const goals = new Map<number, Port>();
    const outward = (side: HandlePosition) => HEADINGS.findIndex(h => h.x === SIDE_NORMALS[side].x && h.y === SIDE_NORMALS[side].y);

    /** The stub is priced like any run: it can cross or end on a link, and one through a node rules its port out. */
    const stubCost = (port: Port, ix: number, iy: number) => {
        // From just clear of the node's own grown outline, or the node would block its own stub.
        const skip = OBSTACLE_CLEARANCE / 2 + 0.5;
        const normal = SIDE_NORMALS[port.side];
        const from = { x: port.point.x + normal.x * skip, y: port.point.y + normal.y * skip };
        if (Math.abs(port.stub.x - port.point.x) + Math.abs(port.stub.y - port.point.y) <= skip) {
            return 0;
        }
        return alongAxis(port.side) === "x"
            ? stepCost("x", ix, from.y, port.stub.y)
            : stepCost("y", iy, from.x, port.stub.x);
    };

    for (const port of sources) {
        const ix = indexOf(xs, port.stub.x);
        const iy = indexOf(ys, port.stub.y);
        if (ix < 0 || iy < 0) continue;
        const cost = port.cost + stubCost(port, ix, iy);
        if (!Number.isFinite(cost)) continue;
        const state = stateOf(ix, iy, outward(port.side));
        if (cost < distance[state]) {
            distance[state] = cost;
            heap.push(cost, state);
        }
    }
    for (const port of targets) {
        const ix = indexOf(xs, port.stub.x);
        const iy = indexOf(ys, port.stub.y);
        if (ix < 0 || iy < 0) continue;
        const cost = stubCost(port, ix, iy);
        if (!Number.isFinite(cost)) continue;
        // Arriving INTO the node: heading opposite to the side's outward normal.
        goals.set(stateOf(ix, iy, (outward(port.side) + 2) % 4), { ...port, cost: port.cost + cost });
    }
    if (!goals.size || !heap.size) {
        return undefined;
    }

    const relax = (from: number, to: number, cost: number) => {
        if (cost < distance[to]) {
            distance[to] = cost;
            previous[to] = from;
            heap.push(cost, to);
        }
    };

    while (heap.size) {
        const [cost, , state] = heap.pop();
        if (cost > distance[state]) continue;
        if (state === END) break;

        const goal = goals.get(state);
        if (goal) {
            relax(state, END, cost + goal.cost);
        }

        const heading = state % 4;
        const cell = (state - heading) / 4;
        const iy = cell % ny;
        const ix = (cell - iy) / ny;

        // Straight on, one grid step.
        const step = HEADINGS[heading];
        const nix = ix + step.x;
        const niy = iy + step.y;
        if (nix >= 0 && nix < nx && niy >= 0 && niy < ny) {
            const moved = step.x
                ? stepCost("y", iy, xs[ix], xs[nix])
                : stepCost("x", ix, ys[iy], ys[niy]);
            if (Number.isFinite(moved)) {
                relax(state, stateOf(nix, niy, heading), cost + moved);
            }
        }

        // Or turn in place.
        relax(state, stateOf(ix, iy, (heading + 1) % 4), cost + COST.bend);
        relax(state, stateOf(ix, iy, (heading + 3) % 4), cost + COST.bend);
    }

    if (!Number.isFinite(distance[END])) {
        return undefined;
    }

    // Walk back from the goal, collecting grid points.  Turns share a point with the step
    // before them; `simplify` drops the duplicates.
    const cells: Point[] = [];
    let state = previous[END];
    const goal = goals.get(state)!;
    let start: Port | undefined;
    while (state >= 0) {
        const heading = state % 4;
        const cell = (state - heading) / 4;
        const iy = cell % ny;
        const ix = (cell - iy) / ny;
        cells.push({ x: xs[ix], y: ys[iy] });
        const before = previous[state];
        if (before < 0) {
            start = sources.find(port =>
                indexOf(xs, port.stub.x) === ix && indexOf(ys, port.stub.y) === iy && outward(port.side) === heading);
        }
        state = before;
    }
    cells.reverse();
    start ??= sources[0];

    return {
        points: straighten([start.point, start.stub, ...cells, goal.stub, goal.point], hard ? solid : []),
        sourceSide: start.side,
        targetSide: goal.side,
        cost: distance[END],
    };
};

const finite = (rect: LayoutRect) =>
    [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite) && rect.width > 0 && rect.height > 0;

/** A route with no search behind it: out, one turn, in.  For sizeless geometry and as the last resort. */
const plainRoute = (source: PortRequest, target: PortRequest): Attempt => {
    const sourceSide = source.sides[0] ?? "right";
    const targetSide = target.sides[0] ?? "left";
    const start = sideMidpoint(source.rect, sourceSide);
    const end = target.fixedAlong === undefined
        ? sideMidpoint(target.rect, targetSide)
        : sidePoint(target.rect, targetSide, target.fixedAlong);
    const exit = { x: start.x + SIDE_NORMALS[sourceSide].x * ROUTE_STUB, y: start.y + SIDE_NORMALS[sourceSide].y * ROUTE_STUB };
    const entry = { x: end.x + SIDE_NORMALS[targetSide].x * ROUTE_STUB, y: end.y + SIDE_NORMALS[targetSide].y * ROUTE_STUB };
    const corner = alongAxis(sourceSide) === "y"
        ? { x: entry.x, y: exit.y }
        : { x: exit.x, y: entry.y };

    return { points: simplify([start, exit, corner, entry, end]), sourceSide, targetSide, cost: 0 };
};

/**
 * Route one link.
 *
 * Tries a box around the two ends first, then the whole chart, then the whole chart with nodes
 * demoted from walls to costs.  The last always succeeds -- the grid is complete once nothing is
 * forbidden -- so a link always gets a route, and `relaxed` says how it was found.
 */
export const routeEdge = (request: RouteRequest): RouteResult => {
    const { source, target } = request;
    if (!finite(source.rect) || !finite(target.rect)) {
        return { ...plainRoute(source, target), relaxed: 3 };
    }

    // The link's own ends are obstacles too, so a route cannot double back through them.
    const solid = [
        ...request.solid,
        { id: source.nodeId, ...source.rect },
        { id: target.nodeId, ...target.rect },
    ];
    const sources = portsOf(source, request.occupancy, request.solid);
    const targets = portsOf(target, request.occupancy, request.solid);

    const chart = expand(request.chart, DETOUR_MARGIN);
    const local = clamp(expand(union(source.rect, target.rect), DETOUR_MARGIN), chart);
    const ladder: [LayoutRect, boolean][] = [[local, true], [chart, true], [chart, false]];

    for (let level = 0; level < ladder.length; level++) {
        const [box, hard] = ladder[level];
        // The local box is the chart box when the ends are far apart; do not search it twice.
        if (level === 1 && box.width === local.width && box.height === local.height) continue;

        const found = attempt(request, solid, box, hard, sources, targets);
        if (found) {
            return { ...found, relaxed: level as 0 | 1 | 2 };
        }
    }

    return { ...plainRoute(source, target), relaxed: 3 };
};
